import { Navigate, Outlet } from 'react-router-dom';

import Link from '@/components/ui/Link';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { ApiError } from '@/lib/api';
import { useSearchParams } from '@/lib/router';
import { spaceHref, spaceHrefKeepingPeriod, spaceSheetHref, type SpaceSheet } from '@/lib/spaces/navigation';
import { SpacePartnerPreviewScope } from './SpacePartnerPreview';

export function SpaceLayout() {
  const { space, spaceId, error, retry } = useSpaceScope();
  if (!spaceId) return <Navigate to="/" replace />;
  if (error) {
    const unavailable = error instanceof ApiError && [403, 404].includes(error.status);
    return <section className="flex flex-col items-start gap-4 py-8" role="alert">
      <h1 className="text-xl font-bold text-text-primary">{unavailable ? 'Espaço indisponível' : 'Não foi possível abrir o espaço'}</h1>
      <p className="text-sm text-text-secondary">{unavailable ? 'Este espaço não está disponível para sua conta.' : 'Verifique sua conexão e tente novamente.'}</p>
      {!unavailable && <Button variant="outline" onClick={retry}>Tentar novamente</Button>}
      <Link href="/" className="cursor-pointer font-semibold text-primary hover:underline">Voltar ao pessoal</Link>
    </section>;
  }
  if (!space) return <div role="status" aria-label="Carregando espaço" aria-busy="true" className="space-y-4">
    <Skeleton className="h-8 w-48 rounded-lg" /><Skeleton className="h-48 w-full rounded-xl" />
  </div>;
  return <div key={spaceId} data-space-scope={space.id} className="isolate min-w-0"><SpacePartnerPreviewScope space={space}><Outlet /></SpacePartnerPreviewScope></div>;
}

export function SpaceHomeRedirect() {
  const { spaceId } = useSpaceScope();
  return <Navigate to={spaceId ? spaceHref(spaceId) : '/'} replace />;
}

// Acerto and Categorias stopped being pages on 2026-09-23: an old link or bookmark opens the home with that sheet.
// Whether the sheet applies (a legacy assigned space has no Acerto) is the home's call, not the address's.
export function SpaceSheetRedirect({ sheet }: Readonly<{ sheet: SpaceSheet }>) {
  const { spaceId } = useSpaceScope();
  return <Navigate to={spaceId ? spaceSheetHref(spaceId, sheet) : '/'} replace />;
}

// The assigned model's "Recorrentes" entry was a placeholder: the space's recurring commitments live in Despesas ›
// A vencer. The redirect keeps the period the address asked for.
export function SpaceUpcomingRedirect() {
  const { spaceId } = useSpaceScope();
  const current = useSearchParams();
  if (!spaceId) return <Navigate to="/" replace />;
  const [path, query = ''] = spaceHrefKeepingPeriod(spaceId, 'expenses', current).split('?');
  const params = new URLSearchParams(query);
  params.set('filter', 'upcoming');
  return <Navigate to={`${path}?${params.toString()}`} replace />;
}
